import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext(null);

// Oscurece un color hex en un porcentaje dado (para derivar gold-deep)
function darken(hex, amount = 0.15) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r   = Math.max(0, (num >> 16) - Math.round(255 * amount));
  const g   = Math.max(0, ((num >> 8) & 0xff) - Math.round(255 * amount));
  const b   = Math.max(0, (num & 0xff) - Math.round(255 * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// Aclara un color hex en un porcentaje dado (para derivar gold-soft)
function lighten(hex, amount = 0.2) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r   = Math.min(255, (num >> 16) + Math.round(255 * amount));
  const g   = Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * amount));
  const b   = Math.min(255, (num & 0xff) + Math.round(255 * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function hexToRgbStr(hex) {
  const num = parseInt(hex.replace('#', ''), 16);
  return `${(num >> 16) & 0xff} ${(num >> 8) & 0xff} ${num & 0xff}`;
}

const COLOR_DEFAULT = '#C8903F';
const LS_COLOR      = 'mezo_color_primario';
const LS_MODO       = 'mezo_modo'; // 'oscuro' | 'claro'

export function ThemeProvider({ children }) {
  const { negocio }                = useAuth();
  const [colorPrimario, _setColor] = useState(() => {
    if (typeof window === 'undefined') return COLOR_DEFAULT;
    return localStorage.getItem(LS_COLOR) ?? COLOR_DEFAULT;
  });
  const [modo, _setModo] = useState(() => {
    if (typeof window === 'undefined') return 'oscuro';
    return localStorage.getItem(LS_MODO) === 'claro' ? 'claro' : 'oscuro';
  });

  // Aplica color + modo en montaje y cada vez que cambien. Esto pone el
  // atributo data-theme en <html> (que activa los overrides en index.css)
  // y setea las CSS variables del gold para que Tailwind las consuma.
  useEffect(() => {
    aplicarModo(modo);
    aplicarColor(colorPrimario);

    // Restaurar tamaño de fuente desde mezo_settings.
    try {
      const settings = JSON.parse(localStorage.getItem('mezo_settings') ?? '{}');
      const mapa = { pequena: '14px', normal: '16px', grande: '18px' };
      if (settings.tamanoFuente && mapa[settings.tamanoFuente]) {
        document.documentElement.style.fontSize = mapa[settings.tamanoFuente];
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si el negocio trae color desde el backend, lo prefiere sobre el local.
  useEffect(() => {
    if (negocio?.colorPrimario) aplicarColor(negocio.colorPrimario);
  }, [negocio]);

  function aplicarModo(nuevoModo) {
    _setModo(nuevoModo);
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    if (nuevoModo === 'claro') {
      html.setAttribute('data-theme', 'claro');
      html.style.colorScheme = 'light';
    } else {
      html.setAttribute('data-theme', 'oscuro');
      html.style.colorScheme = 'dark';
    }
  }

  const toggleModo = useCallback(() => {
    const nuevo = modo === 'claro' ? 'oscuro' : 'claro';
    aplicarModo(nuevo);
    try { localStorage.setItem(LS_MODO, nuevo); } catch {}
  }, [modo]);

  const setModo = useCallback((nuevo) => {
    if (nuevo !== 'claro' && nuevo !== 'oscuro') return;
    aplicarModo(nuevo);
    try { localStorage.setItem(LS_MODO, nuevo); } catch {}
  }, []);

  function aplicarColor(hex) {
    _setColor(hex);
    const root = document.documentElement;
    const deep = darken(hex, 0.15);
    const soft = lighten(hex, 0.2);

    root.style.setProperty('--mezo-gold',      hex);
    root.style.setProperty('--mezo-gold-deep', deep);
    root.style.setProperty('--mezo-gold-soft', soft);
    root.style.setProperty('--mezo-gold-rgb',      hexToRgbStr(hex));
    root.style.setProperty('--mezo-gold-deep-rgb', hexToRgbStr(deep));
    root.style.setProperty('--mezo-gold-soft-rgb', hexToRgbStr(soft));
  }

  function setColorPrimario(hex) {
    aplicarColor(hex);
    try { localStorage.setItem(LS_COLOR, hex); } catch {}
  }

  return (
    <ThemeContext.Provider value={{
      modo,
      modoOscuro: modo === 'oscuro',
      modoClaro:  modo === 'claro',
      toggleModo,
      setModo,
      colorPrimario,
      setColorPrimario,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
